/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import React from 'react';
import * as ReactDOM from 'react-dom';
import type {
  CollectFinancialConnectionsAccountsOverride,
  CollectBankAccountTokenOverride,
} from '@stripe-internal/connect-embedded-lib';
import {
  FinancialConnectionsOverrideProvider,
  isStripeDomain,
} from '@stripe-internal/connect-embedded-lib';
import {StyleSheets} from '@sail/engine';
import {Metrics} from '@sail/observability';
import {FocusContainmentContext} from '@sail/ui';
import type {RequestIframe} from '@sail/ui';
import {uuid} from '../utils/uuid';
import {ConnectElementAttributes} from '../connect/ConnectElementAttributes';
import {ConnectElementRenderedState} from '../connect/ConnectElementRenderedState';
import type {
  ConnectElementConfig,
  ConnectElementImportKeys,
} from '../connect/ConnectJSInterface/ConnectElementList';
import {CONNECT_ELEMENT_IMPORTS} from '../connect/ConnectJSInterface/ConnectElementList';
import {OnboardingState} from '../data-layer-client/OnboardingContext/OnboardingState';
import type {AccountSessionClaim} from '../data-layer-frame/types';
import {DeferredPromise} from '../utils/DeferredPromise';
import initSdkIntl from '../intl/getIntlConfig';
import ConnectSdkProvider from '../connect/ConnectSdkProvider';
import {ConnectElementWrapper} from '../connect/ConnectElementWrapper';
import type {IFrameMessenger} from '../data-layer-client/FrameMessenger';
import {FrameMessenger} from '../data-layer-client/FrameMessenger';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {ConnectJsOptions} from '../connect/ConnectJsOptions';
import {
  getAdoptedStyleSheetApplier,
  getCSSConstructor,
  getCSSStyleSheetClass,
  shouldUseNativeAdoptedStylesheets,
} from '../vendor/construct-style-sheets/src';
import {setupRender} from '../connect/setupRender';
import type {ICalculatedConnectElementProperties} from '../connect/ConnectJSInterface/HtmlEventTypes';
import UILayerMessagePort from './UILayerMessagePort';
import {getDataLayerIframe} from './utils/frameUtils';
import {getDevLogger, getTestmodeLogger} from '../utils/getLogger';
import {getIframeErrorReporter} from './utils/IframeErrorReporter';
import {makeFontFace} from '../connect/fonts/makeFontFace';
import type {SafeFont} from '../connect/fonts/sanitizeFont';
import {sanitizeLoadedFont} from '../connect/fonts/sanitizeFont';
import {livemodeFromPublishableKey} from '../utils/livemodeFromPublishableKey';
import {ConnectElementSupplementalFunctions} from '../connect/ConnectElementSupplementalFunctions';
import {ConnectElementSupplementalObjects} from '../connect/ConnectElementSupplementalObjects';
import {generateSupplementalFunctionsProxy} from './SupplementalFunctionProxy';
import {getObservabilityConfig} from '../connect/getObservabilityConfig';
import {EmbeddedSessionExpiredContextProvider} from '../connect/EmbeddedInvalidOrExpiredSessionContext';
import {
  getAssetSizeAndLoadDurationFromEntries,
  getConnectJsAssetPerformanceEntries,
  onNetworkIdle,
} from '../utils/telemetry/performanceMetrics';
import {LoggedOutState} from '../connect/LoggedOutState';
import {PlatformViewportState} from './PlatformViewportContext/PlatformViewportState';
import {ConnectElementEventEmitter} from '../connect/ConnectElementEventEmitter';
import {analytics} from '../data-layer-frame/analytics';
import {shouldReportRenderError} from './utils/shouldReportRenderError';
import {getTeamForComponent} from '../utils/getTeamForComponent';

const devLogger = getDevLogger();

export class UILayerWrapper {
  /** Time when the UILayerWrapper was created, relative to the Performance.timeOrigin */
  private initialRenderTime = window.performance.now();

  /** Map of StripeJS iframe names to deferred promises, that is resolved when the StripeJS financial connections flow is completed. */
  private deferredActions: Record<string, DeferredPromise<any>> = {};

  /** Map of accessory iframe names to deferred promises, that is resolved when the accessory iframe window object is loaded. */
  private deferredFrames: Record<string, DeferredPromise<Window>> = {};

  /** The deferred promise for the Account Session claim response. */
  private deferredAuthPromise = new DeferredPromise<AccountSessionClaim>();

  /** The HTML attributes of the corresponding embedded component. */
  private connectElementAttributes = new ConnectElementAttributes({});

  /** The Supplemental data functions defined by the platform */
  public supplementalDataFunctions: ConnectElementSupplementalFunctions =
    new ConnectElementSupplementalFunctions({});

  /** The onboarding state of the Connect instance. */
  private onboardingState = new OnboardingState();

  /** The viewport of the platform's frame */
  private platformViewportState = new PlatformViewportState();

  /** The onboarding state of the Connect instance. */
  private loggedOutState = new LoggedOutState();

  /** The rendered state of the corresponding embedded component. */
  private renderedState = new ConnectElementRenderedState();

  /** The ConnectJS options of the Connect instance. */
  private connectJsOptions = new ConnectJsOptions({}, {});

  /** The wrapper around the message port for the corresponding embedded component to communicate with the platform frame. */
  public uiLayerMessagePort: UILayerMessagePort;

  /** The configuration of the corresponding embedded component, including the component chunk to load. */
  private connectElementConfig: ConnectElementConfig;

  /** The style tag containing the font-face declarations for custom fonts. */
  private fontStyleTag: HTMLStyleElement | undefined;

  /** The div tag containing the spans with font attributes to be preloaded. */
  private preloadFontsElement: HTMLElement | null =
    document.getElementById('preload-fonts');

  private currentFinancialConnectionsAccountsPromise: undefined | Promise<any>;

  public supplementalObjects = new ConnectElementSupplementalObjects({});

  /**
   * Creates the React component that is rendered from the UI controller into the corresponding UI layer iframe. Note that the javascript thread still runs on the UI controller iframe.
   * @param embeddedComponent The name of the corresponding embedded component.
   */
  constructor(private embeddedComponent: ConnectElementImportKeys) {
    // Set styles for preloadFontsElement
    if (this.preloadFontsElement) {
      this.preloadFontsElement.style.fontSize = '1px';
      this.preloadFontsElement.style.opacity = '0';
      this.preloadFontsElement.style.height = '2px';
      this.preloadFontsElement.style.width = '2px';
      this.preloadFontsElement.style.position = 'absolute';
      this.preloadFontsElement.style.top = '-2px';
      this.preloadFontsElement.style.pointerEvents = 'none';
      this.preloadFontsElement.style.userSelect = 'none';
      this.preloadFontsElement.style.webkitUserSelect = 'none';
    }

    // Embedded component config
    this.connectElementConfig = CONNECT_ELEMENT_IMPORTS[this.embeddedComponent];

    // Message port listener
    this.uiLayerMessagePort = new UILayerMessagePort(
      this.deferredAuthPromise,
      this.deferredActions,
      this.deferredFrames,
      this.connectElementAttributes,
      this.connectJsOptions,
      this.onboardingState,
      this.platformViewportState,
      this.loggedOutState,
      this.renderedState,
      this.supplementalObjects,
      this.supplementalDataFunctions,
    );

    this.setupStyles();
  }

  private setupStyles() {
    const CssStyleSheetConstructor = getCSSStyleSheetClass();

    const {deferredFrames} = this;
    // When a sail layer is rendered, styles are inserted dynamically
    // Here, we want to know when a style is inserted in the UI layer iframe. Then, we can copy it and send it to the portal iframe
    // This wraps the CSSStyleSheet insertRule function, allowing us to send the rule to the portal iframe as well
    const oldInsertRule = CssStyleSheetConstructor.prototype.insertRule;
    CssStyleSheetConstructor.prototype.insertRule = function (
      rule: string,
      index?: number | undefined,
    ): number {
      Object.values(deferredFrames).forEach(async (deferredFrame) => {
        const frame = await deferredFrame.promise;
        if (frame) {
          // Get the layer name from the UI layer's style tag
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const layerName = (this.ownerNode as Element).getAttribute(
            'data-layer',
          );

          // Find the style tag in the accessory iframe
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const styleTag = frame.document.querySelector(
            `head style[data-layer="${layerName}"]`,
          ) as HTMLStyleElement | undefined;

          if (!styleTag?.sheet) {
            const error = new Error(
              `CSSStyleSheet not found on frame ${frame.name}`,
            );
            getIframeErrorReporter().captureIframeException(error);
            throw error;
          }
          try {
            styleTag.sheet.insertRule(rule, styleTag.sheet.cssRules.length);
          } catch (e) {
            devLogger.error('Error inserting rule in accessory layer:', e);
          }
        }
      });
      // eslint-disable-next-line no-unsafe-finally
      return oldInsertRule.apply(this, [rule, index]);
    };

    if (!shouldUseNativeAdoptedStylesheets()) {
      // Stylesheets can only be configured once
      StyleSheets.config({
        shouldSetHtmlElementId: true,
        styleTarget: 'constructable-sheet',
        cssStyleSheetConstructor: getCSSConstructor,
      });

      // add adoptedStyleSheets property to shadowRoot if needed
      getAdoptedStyleSheetApplier()(document);

      // Ensure styles are piped through to the connect element shadow DOM
      StyleSheets.onFreeze((sheets) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        document.adoptedStyleSheets = sheets
          .map((stylesheet) => stylesheet.sheet)
          .filter(Boolean) as CSSStyleSheet[];
      });

      // set adoptedStyleSheets before first render of sail component
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      document.adoptedStyleSheets = StyleSheets.flattenSheets()
        .map((stylesheet) => stylesheet.sheet)
        .filter(Boolean) as CSSStyleSheet[];
    }
  }

  /**
   * This function creates a style tag that contains the font-face declarations for custom fonts.
   * It then calls `sanitizeLoadedFont` to sanitize the font src for any risky values, enforce HTTPS, etc.
   * Finally, it calls `makeFontFace` to generate the corresponding CSS rule as a string so it can be inserted into the
   * style tag's CSSStyleSheet.
   * @param fonts An array of fonts to be sanitized, that is passed from the platform frame
   */
  private loadFontsInIframe = (
    publishableKey: string,
    fonts: Record<string, unknown>[],
  ) => {
    if (fonts && fonts.length > 0) {
      // Create style tag to store loaded fonts
      const styleTag = document.createElement('style');
      this.fontStyleTag = styleTag;
      styleTag.appendChild(document.createTextNode('/**/'));
      document.head.appendChild(styleTag);

      // Sanitize fonts for risky values, enforce HTTPS, etc.
      const sanitizedFonts: SafeFont[] = [];
      fonts.forEach((font) => {
        try {
          const sanitizedFont = sanitizeLoadedFont(font);
          sanitizedFonts.push(sanitizedFont);
        } catch (error: any) {
          getTestmodeLogger(
            !livemodeFromPublishableKey(publishableKey, true),
          ).warn(error.message);

          getIframeErrorReporter().captureIframeException(error);
        }
      });

      // Create font-face rules and append them to the style tag's CSSStyleSheet
      const fontFaceRules = makeFontFace(sanitizedFonts);
      fontFaceRules.forEach((declaration) => {
        if (!styleTag.sheet) {
          const error = new Error('UI layer font stylesheet not found');
          getIframeErrorReporter().captureIframeException(error);
          throw error;
        }
        styleTag.sheet.insertRule(declaration);
      });
    }
  };

  /**
   * This function grabs the font-face declarations from the UI layer's style tag and sends them to the accessory iframe.
   * @param iframe The window object of the accessory iframe
   */
  private copyFontsToIframe = (iframe: Window) => {
    if (this.fontStyleTag) {
      if (!this.fontStyleTag.sheet) {
        const error = new Error('UI layer font stylesheet not found');
        getIframeErrorReporter().captureIframeException(error);
        throw error;
      }

      const accessoryLayerStyleTag = iframe.document.createElement('style');
      accessoryLayerStyleTag.appendChild(
        iframe.document.createTextNode('/**/'),
      );
      iframe.document.head.appendChild(accessoryLayerStyleTag);
      if (!accessoryLayerStyleTag.sheet) {
        const error = new Error('Accessory layer font stylesheet not found');
        getIframeErrorReporter().captureIframeException(error);
        throw error;
      }
      const rules = this.fontStyleTag.sheet.cssRules;
      for (let i = 0; i < rules.length; i += 1) {
        const rule = rules[i];
        accessoryLayerStyleTag.sheet.insertRule(rule.cssText, i);
      }
    }
  };

  /**
   * This function grabs the background color from the connectJSOptions object and sets it on the ui layer iframe.
   */
  private copyBackgroundToUILayerFrame = () => {
    document.body.style.backgroundColor =
      this.connectJsOptions.getValues().appearance?.variables
        ?.colorBackground ?? 'transparent';
  };

  private requestIframe: RequestIframe = async (
    id,
    type,
    initialReposition,
  ) => {
    const CssStyleSheetConstructor = getCSSStyleSheetClass();

    if (!this.deferredFrames[id]) {
      // Only request a new frame if we're not already waiting for one with the same id
      this.deferredFrames[id] = new DeferredPromise<Window>();
      this.uiLayerMessagePort.requestAccessoryLayer(
        id,
        type,
        initialReposition,
      );
    }

    const iframe = await this.deferredFrames[id].promise;

    // Set the inherited font in the accessory iframe body to the same as the UI layer. This ensures font style inheritance, even
    // when the specified font is invalid
    iframe.document.body.style.fontFamily = document.body.style.fontFamily;

    // Copy NumberExfiltration TODO to the accessory iframe
    if (this.preloadFontsElement) {
      const clone = this.preloadFontsElement.cloneNode(true); // true means clone all childNodes and all event handlers
      iframe.document.body.appendChild(clone);
    }

    // Send custom font styles to the accessory iframe
    this.copyFontsToIframe(iframe);

    // We have to send the Sail stylesheets from the UI frame to the accessory frame
    const stylesheets = StyleSheets.flattenSheets();
    const rules: string[] = [];
    const layerNames: string[] = [];
    stylesheets.forEach((stylesheet) => {
      const cssStylesheet = stylesheet.sheet;
      if (!cssStylesheet) {
        devLogger.error('CSSStyleSheet not found on UI layer');
        return;
      }

      const layerName = stylesheet.name;
      let payload = '';
      const {cssRules} = cssStylesheet;
      for (let i = 0; i < cssRules.length; i += 1) {
        payload += `${cssRules[i].cssText}\n`;
      }
      rules.push(payload);
      layerNames.push(layerName);
    });

    // Copy stylesheets
    rules.forEach((cssText: string, index: number) => {
      const stylesheet = new CssStyleSheetConstructor();
      stylesheet.replaceSync(cssText);

      // It is important to create the style tag with the text node, instead of appending an empty style tag
      // as this results in us having to specify 2 SHAs to allowlist for our style-src CSP directives,
      // one for /**/ and another for a blank style tag.
      const styleTag = iframe.document.createElement('style');
      styleTag.appendChild(iframe.document.createTextNode('/**/'));
      styleTag.setAttribute('data-layer', layerNames[index]);
      styleTag.setAttribute('type', 'text/css');
      iframe.document.querySelector('head')!.appendChild(styleTag);
      const newSheet = styleTag.sheet;
      if (!newSheet) {
        const error = new Error('CSSStyleSheet not found on UI layer');
        getIframeErrorReporter().captureIframeException(error);
        throw error;
      }

      const rules = stylesheet.cssRules;
      for (let i = 0; i < rules.length; i += 1) {
        const rule = rules[i];
        newSheet.insertRule(rule.cssText, newSheet.cssRules.length);
      }
    });

    return {
      iframe,
      uiLayerMessagePort: this.uiLayerMessagePort,
    };
  };

  private collectFinancialConnectionsAccountsOverride: CollectFinancialConnectionsAccountsOverride =
    async ({
      clientSecret,
      locale,
      merchantId,
      publishableKey,
      stripeJsEndpoint,
    }) => {
      // This condition should never be triggered, merchantId has a type of {string | undefined}, but because this call is always preceeded by an
      // api call to useCreateLinkAccountSessionMutation this function can only be called once authInfo is available. So merchantId should always have the type {string}
      if (!merchantId) {
        return {
          financialConnectionsSession: undefined,
          error: {
            type: 'invalid_request_error',
            message: 'Connected Account Id is undefined',
          },
        };
      }

      if (this.currentFinancialConnectionsAccountsPromise) {
        return this.currentFinancialConnectionsAccountsPromise;
      }

      const id = uuid();
      const deferredPromise = new DeferredPromise<any>();
      this.deferredActions[id] = deferredPromise;
      this.currentFinancialConnectionsAccountsPromise = deferredPromise.promise;

      this.uiLayerMessagePort.collectFinancialConnectionsAccounts({
        id,
        clientSecret,
        locale,
        merchantId,
        publishableKey,
        stripeJsEndpoint,
      });

      const result = await deferredPromise.promise;
      delete this.deferredActions[id];
      this.currentFinancialConnectionsAccountsPromise = undefined;

      this.uiLayerMessagePort.deleteStripeJsLayer(id);
      return result;
    };

  private collectBankAccountTokenOverride: CollectBankAccountTokenOverride =
    async ({
      clientSecret,
      locale,
      merchantId,
      publishableKey,
      stripeJsEndpoint,
    }) => {
      // This condition should never be triggered, merchantId has a type of {string | undefined}, but because this call is always preceeded by an
      // api call to useCreateLinkAccountSessionMutation this function can only be called once authInfo is available. So merchantId should always have the type {string}
      if (!merchantId) {
        return {
          financialConnectionsSession: undefined,
          token: undefined,
          error: {
            type: 'invalid_request_error',
            message: 'Connected Account Id is undefined',
          },
        };
      }

      if (this.currentFinancialConnectionsAccountsPromise) {
        return this.currentFinancialConnectionsAccountsPromise;
      }

      const id = uuid();
      const deferredPromise = new DeferredPromise<any>();
      this.deferredActions[id] = deferredPromise;
      this.currentFinancialConnectionsAccountsPromise = deferredPromise.promise;

      this.uiLayerMessagePort.collectBankAccountToken({
        id,
        clientSecret,
        locale,
        merchantId,
        publishableKey,
        stripeJsEndpoint,
      });

      const result = await deferredPromise.promise;
      delete this.deferredActions[id];
      this.currentFinancialConnectionsAccountsPromise = undefined;

      this.uiLayerMessagePort.deleteStripeJsLayer(id);
      return result;
    };

  public initUILayer = async () => {
    const [deferredPort, deferredInit] =
      this.uiLayerMessagePort.initMessagePort();

    const [
      {
        fontFamily,
        htmlAttributes,
        initOptions,
        publishableKey,
        timeToInitializeUILayer,
        jsLoadToInitUILayer,
        connectInstanceId,
        loadedFonts,
        supplementalObjects,
        supplementalFunctionValues,
        onboardingStateValues,
        loggedOutStateValues,
        platformViewportValues,
        browserSecureContext,
      },
      {origin},
    ] = await Promise.all([Promise.resolve(deferredInit), deferredPort]);

    // Event emitter
    const emitEvent = (
      event: CustomEvent<ICalculatedConnectElementProperties>,
    ) => {
      const {type, detail} = event;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      this.uiLayerMessagePort.emitHTMLEvent({
        eventType: type,
        detail,
      } as any); // TODO: improve typing
    };

    const eventEmitter = new ConnectElementEventEmitter(
      emitEvent,
      this.embeddedComponent,
    );

    // Setting up the frame messenger variable here to ensure it is not scoped to the try block
    let frameMessenger: IFrameMessenger | null = null;

    const project = getTeamForComponent(this.embeddedComponent);
    try {
      // Report that the message port has been initialized successfully
      this.uiLayerMessagePort.reportSuccess('messagePort');

      if (
        initOptions.appearance?.variables?.fontFamily &&
        this.preloadFontsElement
      ) {
        this.preloadFontsElement.style.fontFamily =
          initOptions.appearance.variables.fontFamily;
      }
      this.loadFontsInIframe(publishableKey, loadedFonts);

      const [componentDynamicImport, intlConfig] = await Promise.all([
        this.connectElementConfig.Component(),
        initSdkIntl(initOptions.locale),
      ]);

      // Initialize onboarding state
      this.onboardingState.updateValues(onboardingStateValues);

      // Initialize platform viewport state
      this.platformViewportState.updateValues(platformViewportValues);

      // Initialize logged out state
      this.loggedOutState.updateValues(loggedOutStateValues);

      // Initialize ConnectJS options
      this.connectJsOptions.updateValues(initOptions);

      // Initialize supplemental function values
      this.supplementalDataFunctions.updateValues(
        generateSupplementalFunctionsProxy(
          supplementalFunctionValues,
          this.uiLayerMessagePort,
        ),
      );

      const Component = componentDynamicImport.default;

      // Onboarding state
      this.onboardingState.registerObserver(() => {
        this.uiLayerMessagePort.updateOnboardingState(
          this.onboardingState.values,
        );
      });

      // Inherit font style from closest parent div
      document.body.style.fontFamily = fontFamily;

      if (!this.connectElementConfig.ignoreThemingValues) {
        // Set background color on UI Layer to the specified value from initOptions, this is
        // needed before the observer is registered to ensure the background color is set initially
        this.copyBackgroundToUILayerFrame();
        this.connectJsOptions.registerObserver(() => {
          this.copyBackgroundToUILayerFrame();
        });
      }

      // Initialize HTML attributes
      this.connectElementAttributes.updateValues(htmlAttributes);

      // Initialize supplemental objects
      this.supplementalObjects.updateValues(supplementalObjects);

      // Create frame messenger
      frameMessenger = new FrameMessenger(
        connectInstanceId,
        // Note: Since web components don't run in the context of the connect.js script
        // we capture and pass on connect.js script information here
        getCurrentScriptUrlContext(),
        publishableKey,
        initOptions.metaOptions,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        // Handles the refreshClientSecretCallback on the UI layer. This will post a message to the platform frame, which calls
        // the platform frame's FrameMessenger instance's refreshClientSecretCallback. This will also await the message response
        // from the platform frame so that the Data layer is updated with the new API key before making the API request.
        // This is the messaging flow:
        // - UI layer --> Platform frame ('refreshclientsecretfrom' informs the platform frame to check whether it should refresh the client secret)
        // - Platform frame --> Data layer ('authenticate' only if the client secret needs to be refreshed)
        // - Data layer --> Platform frame (informs the platform frame that the claim API has been called)
        // - Platform frame --> UI layer ('refreshclientsecretto' informs the UI layer that the API request can be made since the Data layer has been updated with the new API key)
        () =>
          this.uiLayerMessagePort.sendMessageAndWaitForReply(
            'refreshclientsecretfrom',
          ),
      );

      analytics.track('submerchant_surfaces_render_begin', {
        connectElement: this.embeddedComponent,
        hostApp: initOptions.metaOptions?.hostApp ?? 'platform',
        teamName: project,
      });

      // Wait for data layer to have loaded
      await this.uiLayerMessagePort.hasDataLayerLoaded.promise;

      const dataLayerIframeWindow = getDataLayerIframe(connectInstanceId);
      if (dataLayerIframeWindow === null) {
        const error = new Error('The data layer iframe is not defined');
        getIframeErrorReporter().captureIframeException(error);
        throw error;
      }
      frameMessenger.initWithExistingWindow(dataLayerIframeWindow);

      const {fetch, reportError, reportErrorOrSuccessLink, queryClient} =
        setupRender({
          connectElementKey: this.embeddedComponent,
          connectJsOptions: this.connectJsOptions,
          frameMessenger,
        });
      ReactDOM.render(
        <EmbeddedSessionExpiredContextProvider>
          <ConnectSdkProvider
            fetch={fetch}
            reportError={reportError}
            reportErrorOrSuccessLink={reportErrorOrSuccessLink}
            intlConfig={intlConfig}
            connectJsOptions={this.connectJsOptions}
            dataLayerFrame={dataLayerIframeWindow}
            connectElement={this.embeddedComponent}
            frameMessenger={frameMessenger}
            v2WorkspaceIdOverride={
              initOptions?.metaOptions?.v2WorkspaceIdOverride
            }
          >
            <ConnectElementWrapper
              fetchOverride={fetch}
              queryClient={queryClient}
              deferredAuthPromise={this.deferredAuthPromise}
              publishableKey={publishableKey}
              connectElement={this.embeddedComponent}
              connectElementAttributes={this.connectElementAttributes}
              eventEmitter={eventEmitter}
              renderedState={this.renderedState}
              frameMessenger={frameMessenger}
              onboardingState={this.onboardingState}
              platformViewportState={this.platformViewportState}
              loggedOutState={this.loggedOutState}
              /**
               * In ComponentLoadMetric, we do window.performance.now() - the time to render.
               * However, we have to include the time it took to initialize the UI layer frame from the
               * platform frame. Hence, we pass in the timeToInitializeUILayer to account for this,
               * resulting in the formula:
               * window.performance.now() - (this.initialRenderTime - timeToInitializeUILayer)
               * window.performance.now() - this.initialRenderTime + timeToInitializeUILayer
               */
              initialRenderTime={
                this.initialRenderTime - timeToInitializeUILayer
              }
              connectJsLoadStartTime={
                jsLoadToInitUILayer
                  ? this.initialRenderTime - jsLoadToInitUILayer
                  : undefined
              }
              metaOptions={initOptions.metaOptions}
              connectJsOptions={this.connectJsOptions}
              updateConnectJSOptions={(options) =>
                this.uiLayerMessagePort.updateConnectJSOptions(options)
              }
              isInternalDomain={!!isStripeDomain(new URL(origin))}
              requestIframe={this.requestIframe}
              platformFrameFunctions={{
                copyTextToClipboard: (text) =>
                  this.uiLayerMessagePort.copyText(text),
                reloadWindow: () => this.uiLayerMessagePort.reloadWindow(),
                removeSearchParam: (param) =>
                  this.uiLayerMessagePort.removeSearchParam(param),
                redirectWindow: (url) =>
                  this.uiLayerMessagePort.redirectWindow(url),
              }}
              connectElementSupplementalFunctions={
                this.supplementalDataFunctions
              }
              connectElementSupplementalObjects={this.supplementalObjects}
              browserSecureContext={browserSecureContext}
            >
              <FocusContainmentContext.Consumer>
                {(focusContext) => {
                  return (
                    <FinancialConnectionsOverrideProvider
                      value={{
                        collectFinancialConnectionsAccounts: async (
                          options,
                        ) => {
                          focusContext.setContain(false);
                          const result =
                            await this.collectFinancialConnectionsAccountsOverride(
                              options,
                            );
                          focusContext.setContain(true);
                          return result;
                        },
                        collectBankAccountToken: async (options) => {
                          focusContext.setContain(false);
                          const result =
                            await this.collectBankAccountTokenOverride(options);
                          focusContext.setContain(true);
                          return result;
                        },
                      }}
                    >
                      <Component />
                    </FinancialConnectionsOverrideProvider>
                  );
                }}
              </FocusContainmentContext.Consumer>
            </ConnectElementWrapper>
          </ConnectSdkProvider>
        </EmbeddedSessionExpiredContextProvider>,
        document.getElementById('root'),
      );
    } catch (error: any) {
      // Verify that the object caught is truthy (i.e. not null, undefined, etc.)
      if (error) {
        const errorType = 'render_error';

        let errorMessage: string;
        if (error instanceof Error) {
          errorMessage = error.message;
        } else {
          // This is needed if a non-Error object is thrown by our code
          errorMessage = String(error);
        }

        eventEmitter.emitEvent('loaderror', {
          error: {
            type: errorType,
            message: errorMessage,
          },
        });
        eventEmitter.emitEvent('_internal_loaderror', {
          error,
          type: errorType,
        });

        if (shouldReportRenderError(errorMessage)) {
          // Convert the error to an Error object if it is not already
          const renderError = error instanceof Error ? error : new Error(error);
          getIframeErrorReporter().captureIframeException(renderError);

          analytics.track('submerchant_surfaces_render_error', {
            error_message: error?.message,
            error_stack: error?.stack,
            connectElement: this.embeddedComponent,
            hostApp: initOptions.metaOptions?.hostApp ?? 'platform',
            teamName: project,
            errorSource: 'loadError',
          });
        }
      }
    }

    this.uiLayerMessagePort.reportSuccess('render');

    if (frameMessenger) {
      const observabilityConfig = getObservabilityConfig({
        connectElement: this.embeddedComponent,
        frameMessenger,
        metaOptions: initOptions.metaOptions,
      });

      const metrics = new Metrics(observabilityConfig);

      if (window.performance) {
        const firstMountDuration = Math.ceil(
          window.performance.now() -
            this.initialRenderTime +
            timeToInitializeUILayer,
        );
        onNetworkIdle(() => {
          const {totalAssetSize, assetLoadStart, assetLoadEnd} =
            getAssetSizeAndLoadDurationFromEntries(
              getConnectJsAssetPerformanceEntries(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                window.performance.getEntriesByType(
                  'resource',
                ) as PerformanceResourceTiming[],
              ),
            );

          metrics.gauge('submerchant_surfaces_component_timings', {
            /**
             * We include the `timeToInitializeUILayer` to account for the time it takes to run the relevant
             * Javascript on the platform frame and load the UI layer iframe.
             * We can't use the `window.performance.now()` on the UI layer to subtract the `timeToInitializeUILayer`
             * since the now() method returns a timestamp representing the time elapsed since Performance.timeOrigin (which is
             * the time when navigation has started in window contexts), which would be different on the platform
             * frame and the UI layer frame.
             */
            first_mount_duration: firstMountDuration,
            component_js_total_network_transfer_size: totalAssetSize,
            component_js_total_network_duration: assetLoadEnd - assetLoadStart,
          });
        });
      }
    }
  };
}
